import React from "react";
import PropTypes from "prop-types";
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";

const MODES = [
    { value: "standard", label: "5v5" },
    { value: "wingman", label: "2v2 Wingman" },
];

const WINGMAN_TEAM_SIZE = 2;

export default function LobbyQueueModeSelector({
    queueMode = "standard",
    isLeader = false,
    disabled = false,
    memberCount = 1,
    onSelect,
}) {
    const resolvedQueueMode = queueMode === "wingman" ? "wingman" : "standard";
    const wingmanBlockedReason = !isLeader
        ? "Only the lobby leader can switch queue mode."
        : memberCount > WINGMAN_TEAM_SIZE
            ? `Wingman parties are limited to ${WINGMAN_TEAM_SIZE} players.`
            : null;

    return (
        <Box
            className="lobbyQueueModeSelector"
            sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                flex: "0 0 auto",
            }}
        >
            <ToggleButtonGroup
                exclusive
                size="small"
                value={resolvedQueueMode}
                onChange={(_, nextMode) => {
                    if (!nextMode || nextMode === resolvedQueueMode) return;
                    if (disabled || wingmanBlockedReason) return;
                    onSelect?.(nextMode);
                }}
                sx={{
                    "& .MuiToggleButton-root": {
                        px: { xs: "1rem", xl: "1.35rem" },
                        py: "0.3rem",
                        fontSize: "0.8rem",
                        letterSpacing: "0.4px",
                        textTransform: "none",
                        color: "rgba(255, 255, 255, 0.65)",
                        borderColor: "rgba(255, 255, 255, 0.12)",
                        "&.Mui-selected": {
                            color: "#ffffff",
                            backgroundColor: "rgba(232, 163, 61, 0.2)",
                            borderColor: "rgba(232, 163, 61, 0.6)",
                            "&:hover": {
                                backgroundColor: "rgba(232, 163, 61, 0.25)",
                            },
                        },
                        "&:hover": {
                            backgroundColor: "rgba(232, 163, 61, 0.1)",
                        },
                    },
                }}
            >
                {MODES.map((mode) => {
                    const isWingmanBlocked =
                        mode.value === "wingman" &&
                        Boolean(wingmanBlockedReason);

                    const button = (
                        <ToggleButton
                            key={mode.value}
                            value={mode.value}
                            disabled={disabled || isWingmanBlocked}
                            aria-label={`${mode.label} queue`}
                        >
                            {mode.label}
                        </ToggleButton>
                    );

                    return isWingmanBlocked ? (
                        <Tooltip key={mode.value} title={wingmanBlockedReason}>
                            <span>{button}</span>
                        </Tooltip>
                    ) : (
                        button
                    );
                })}
            </ToggleButtonGroup>
        </Box>
    );
}

LobbyQueueModeSelector.propTypes = {
    queueMode: PropTypes.string,
    isLeader: PropTypes.bool,
    disabled: PropTypes.bool,
    memberCount: PropTypes.number,
    onSelect: PropTypes.func,
};
